import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import Immutable from 'immutable'
import moment from 'moment'
import type { Dispatch } from 'redux'

import { BROWSER_ERROR_KEYS, instrumentBrowserError } from '@library/observability/browser'

import { trackSignupStarted } from 'actions/loggingmanager'
import { fetchDeliveryConsignment } from 'apis/deliveries'
import * as prospectApi from 'apis/prospect'
import type { ShippingAddress } from 'apis/user'
import * as userApi from 'apis/user'
import type { BasketState } from 'reducers/basket'
import { deleteOrder } from 'routes/Account/MyDeliveries/apis/orderV2'
import { fetchProjectedDeliveries, skipDates } from 'routes/Account/apis/subscription'
import { getEmail, getIsEmailMarketingEnabled, getName } from 'routes/Checkout/checkoutSelectors'
import type { CheckoutStore } from 'routes/Checkout/models/CheckoutStore'
import * as orderV2 from 'routes/Menu/apis/orderV2'
import { getAuthUserId } from 'selectors/auth'
import { getPreviewOrderId, getPromoCode } from 'selectors/basket'
import { getUserId } from 'selectors/user'
import { GoustoException } from 'utils/GoustoException'
import { canUseWindow } from 'utils/browserEnvironment'
import type { LogLevel } from 'utils/logger'
import logger from 'utils/logger'
import { transformPendingOrders, transformProjectedDeliveries } from 'utils/myDeliveries'
import type { KeyedGetter } from 'utils/types/immutable'
import type { ProjectedDelivery, UserOrder } from 'utils/types/order'

import { actionTypes } from './actionTypes'
import { basketAddressChange, basketChosenAddressChange, basketPostcodeChangePure } from './basket'
import statusActions from './status'
import { subscriptionLoadData } from './subscription'
import { trackUserAttributes } from './tracking'

const fetchShippingAddressesPending = (pending: boolean) => ({
  type: actionTypes.USER_SHIPPING_ADDRESSES_PENDING,
  pending,
})

const fetchedShippingAddresses = (shippingAddresses: ShippingAddress[]) => ({
  type: actionTypes.USER_SHIPPING_ADDRESSES_RECEIVE,
  shippingAddresses,
})

const fetchShippingAddressesError = (message: string) => ({
  type: actionTypes.USER_SHIPPING_ADDRESSES_ERROR,
  message,
})

const userLoadReferralDetails = (referralDetails?: {
  referralCount: number
  referralCredit: number
}) => ({
  type: actionTypes.USER_LOAD_REFERRAL_DETAILS,
  referralDetails,
})

type GetState = <T = any>() => T

function userFetchCredit() {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    const accessToken =
      getState<{ auth: KeyedGetter<{ accessToken: string }> }>().auth.get('accessToken')

    try {
      const { data: userCreditData } = await userApi.fetchUserCredit(accessToken)
      const { balance: userCredit } = userCreditData

      dispatch({
        type: actionTypes.USER_CREDIT,
        userCredit,
      })
    } catch (err) {
      dispatch(statusActions.error(actionTypes.USER_CREDIT, (err as any).message))
      logger.error(err as any)
      throw err
    } finally {
      dispatch(statusActions.pending(actionTypes.USER_CREDIT, false))
    }
  }
}

function userLoadOrdersFactory(
  actionType:
    | typeof actionTypes.USER_LOAD_ORDERS
    | typeof actionTypes.USER_LOAD_ORDERS_WITH_RESTORABLE,
  storePropertyName: 'orders' | 'restorableOrders' = 'orders',
  location: 'MENU' | 'DELIVERIES' | 'MY_GOUSTO' = 'MENU',
) {
  return function loadOrders(
    forceRefresh = false,
    orderType = 'pending',
    number = 10,
    extendsParams: unknown[] = [],
  ) {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
      dispatch(statusActions.pending(actionType, true))
      dispatch(statusActions.error(actionType, null))
      try {
        const existingOrders = getState<{
          user: KeyedGetter<{
            orders: Immutable.OrderedMap<string, UserOrder>
            restorableOrders: Immutable.OrderedMap<string, UserOrder>
          }>
        }>().user.get(storePropertyName, Immutable.OrderedMap({}))

        if (forceRefresh || !existingOrders.size) {
          const payload = {
            limit: number,
            sort_order: 'desc',
            state: orderType,
            includes: ['shipping_address'],
            extends: extendsParams,
          }
          const response = await orderV2.fetchUserOrders(dispatch, getState, payload)
          const orders = response.data || []

          dispatch({
            type: actionType,
            orders,
            forceRefresh,
          })
        }
      } catch (err) {
        instrumentBrowserError(BROWSER_ERROR_KEYS.FAILED_TO_LOAD_ORDERS, {
          source: 'loadOrders',
          actionType,
          orderType,
          forceRefresh,
          errorTag: `USER_ORDERS_LOAD_FAIL_${location}`,
          error: err,
        })

        dispatch(statusActions.error(actionType, err))
      }
      dispatch(statusActions.pending(actionType, false))
    }
  }
}

const userLoadOrders = userLoadOrdersFactory(actionTypes.USER_LOAD_ORDERS, 'orders', 'MENU')
const userLoadOrdersForMyDeliveriesPage = userLoadOrdersFactory(
  actionTypes.USER_LOAD_ORDERS_WITH_RESTORABLE,
  'restorableOrders',
  'DELIVERIES',
)
const userLoadOrdersForMyGousto = userLoadOrdersFactory(
  actionTypes.USER_LOAD_ORDERS,
  'orders',
  'MY_GOUSTO',
)

export function userLoadProjectedDeliveries(forceRefresh = false) {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    dispatch(statusActions.pending(actionTypes.USER_LOAD_PROJECTED_DELIVERIES, true))
    dispatch(statusActions.error(actionTypes.USER_LOAD_PROJECTED_DELIVERIES, null))

    try {
      const state = getState<{
        auth: KeyedGetter<{ accessToken: string }>
        user: KeyedGetter<{
          projectedDeliveries: Immutable.List<ProjectedDelivery>
        }>
      }>()
      const accessToken = state.auth.get('accessToken')
      const userId: string = getUserId(state)

      const dispatchProjectedDeliveries = (
        projectedDeliveries: {
          deliveryDate: string
          menuOpenDate: string
          skipped: boolean
        }[],
      ) => {
        dispatch({
          type: actionTypes.USER_LOAD_PROJECTED_DELIVERIES,
          projectedDeliveries,
        })
      }

      if (forceRefresh || !state.user.get('projectedDeliveries').size) {
        if (userId) {
          const { data } = await fetchProjectedDeliveries(accessToken, userId)

          dispatchProjectedDeliveries(data.data.projectedDeliveries)
        } else {
          dispatchProjectedDeliveries([])
        }
      }
    } catch (err) {
      dispatch(statusActions.error(actionTypes.USER_LOAD_PROJECTED_DELIVERIES, err))
      logger.error(err as any)
      throw err
    } finally {
      dispatch(statusActions.pending(actionTypes.USER_LOAD_PROJECTED_DELIVERIES, false))
    }
  }
}

function userVerifyAge(verified: boolean, hardSave: boolean) {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    dispatch(statusActions.pending(actionTypes.USER_AGE_VERIFY, true))

    try {
      if (verified && hardSave) {
        const accessToken = getState<{
          auth: KeyedGetter<{ accessToken: string }>
        }>().auth.get('accessToken')
        await userApi.verifyAge(accessToken, 'current')
      }

      dispatch({
        type: actionTypes.USER_AGE_VERIFY,
        verified,
      })
    } catch (err) {
      dispatch(statusActions.error(actionTypes.USER_AGE_VERIFY, (err as any).message))
      dispatch(statusActions.pending(actionTypes.USER_AGE_VERIFY, false))
      logger.error(err as any)
      throw err
    }
    dispatch(statusActions.pending(actionTypes.USER_AGE_VERIFY, false))
  }
}

function userOrderCancelNext() {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    dispatch(statusActions.pending(actionTypes.USER_ORDER_CANCEL_NEXT, true))
    dispatch(statusActions.error(actionTypes.USER_ORDER_CANCEL_NEXT, false))
    const errorPrefix = 'Order skip projected error:'
    const cancellablePhases = ['awaiting_choices', 'open', 'pre_menu']

    try {
      await dispatch(userLoadOrders())

      const state = getState<{
        auth: KeyedGetter<{ accessToken: string }>
        user: KeyedGetter<{
          orders: Immutable.List<UserOrder>
        }>
      }>()

      const cancellableOrder = state.user
        .get('orders')
        .filter((order) => cancellablePhases.includes(order?.get('phase') || ''))

      if (cancellableOrder.size) {
        const orderToCancelId = cancellableOrder
          .sort((orderA, orderB) => {
            // Refactoring note: I don't see a deliveryDay field on the order object
            const orderDateA = orderA.get('deliveryDay' as unknown as any)
            const orderDateB = orderB.get('deliveryDay' as unknown as any)

            return (
              (moment(orderDateA) as unknown as number) - (moment(orderDateB) as unknown as number)
            )
          })
          .first()
          .get('id')

        try {
          const accessToken = state.auth.get('accessToken')
          const userId = getAuthUserId(state)

          await deleteOrder(accessToken, orderToCancelId, userId)

          dispatch({
            type: actionTypes.USER_UNLOAD_ORDERS,
            orderIds: [orderToCancelId],
          })
        } catch (err) {
          throw new GoustoException(
            `${errorPrefix} attempt to cancel delivery ${orderToCancelId} failed`,
          )
        }
      } else {
        throw new GoustoException(`${errorPrefix} no orders found to cancel`, {
          error: 'no-orders-found',
          level: 'warning',
        })
      }
    } catch (err) {
      const message = (err as any).message || `${errorPrefix} ${err}`
      const error = (err as any).error || message
      const logLevel: LogLevel = (err as any).level || 'error'

      logger[logLevel](message)
      dispatch(statusActions.error(actionTypes.USER_ORDER_CANCEL_NEXT, error))
    } finally {
      dispatch(statusActions.pending(actionTypes.USER_ORDER_CANCEL_NEXT, false))
    }
  }
}

function userOrderSkipNextProjected() {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    dispatch(statusActions.pending(actionTypes.USER_ORDER_SKIP_NEXT_PROJECTED, true))
    dispatch(statusActions.error(actionTypes.USER_ORDER_SKIP_NEXT_PROJECTED, false))
    const errorPrefix = 'Order skip projected error:'

    try {
      const state = getState<{
        user: KeyedGetter<{
          projectedDeliveries: Immutable.List<KeyedGetter<ProjectedDelivery>>
        }>
        auth: KeyedGetter<{ accessToken: string }>
      }>()
      let projectedOrders = state.user.get('projectedDeliveries')

      if (!projectedOrders.size) {
        await dispatch(userLoadProjectedDeliveries())
        projectedOrders = state.user.get('projectedDeliveries')
      }

      if (!projectedOrders.size) {
        throw new GoustoException(`${errorPrefix} no orders found to skip`, {
          error: 'no-orders-found',
          level: 'warning',
        })
      }

      // Refactoring note: I'm quite confident that this is broken
      // This code is not called outside of tests either
      const orderToSkipId = projectedOrders.first().get('id' as any)
      const accessToken = state.auth.get('accessToken')

      try {
        const orderToSkipDate = projectedOrders
          .first()
          .get('deliveryDate' as any)
          .split(' ')[0]
        const userId = getUserId(state)

        await skipDates(accessToken, userId, [orderToSkipDate])

        dispatch({
          type: actionTypes.USER_UNLOAD_PROJECTED_DELIVERIES,
          deliveryDayIds: [orderToSkipId],
        })
      } catch (err) {
        throw new GoustoException(`${errorPrefix} attempt to skip delivery ${orderToSkipId} failed`)
      }
    } catch (err) {
      const message = (err as any).message || `${errorPrefix} ${err}`
      const error = (err as any).error || message
      const logLevel: LogLevel = (err as any).level || 'error'

      logger[logLevel](message)
      dispatch(statusActions.error(actionTypes.USER_ORDER_SKIP_NEXT_PROJECTED, error))
    } finally {
      dispatch(statusActions.pending(actionTypes.USER_ORDER_SKIP_NEXT_PROJECTED, false))
    }
  }
}

function userLoadData() {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    const state = getState<{
      auth: Immutable.Map<string, string>
    }>()
    let user: { id: string } | undefined

    try {
      const { data } = await userApi.fetchUser(state.auth.get('accessToken'))
      user = data?.user ?? undefined
    } catch (err) {
      const auth = state.auth.toJS() as {
        isAdmin: boolean
        hasRefreshCookie: boolean
        rememberMe: boolean
        isAuthenticated: boolean
        expiresAt: string
        name: string
        accessToken: string
        id: string
        email: string
        roles: string[]
      }
      instrumentBrowserError(BROWSER_ERROR_KEYS.FAILED_TO_LOAD_USER, {
        source: 'userLoadData',
        isAdmin: auth.isAdmin,
        hasRefreshCookie: auth.hasRefreshCookie,
        rememberMe: auth.rememberMe,
        isAuthenticated: auth.isAuthenticated,
        accessToken: `${auth.accessToken.slice(0, 5)}***${auth.accessToken.slice(-5)}`,
        id: auth.id,
        roles: auth.roles,
        error: err,
      })
      throw err
    }

    dispatch({
      type: actionTypes.USER_LOAD_DATA,
      user,
    })

    if (canUseWindow()) {
      dispatch(trackUserAttributes())

      try {
        datadogLogs.setGlobalContextProperty('userID', user?.id)
        datadogRum.setGlobalContextProperty('userID', user?.id)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error setting DD context:', err)
      }
    }

    dispatch(subscriptionLoadData())
  }
}

function userFetchShippingAddresses() {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    dispatch(fetchShippingAddressesPending(true))
    try {
      const accessToken =
        getState<{ auth: KeyedGetter<{ accessToken: string }> }>().auth.get('accessToken')
      const { data: shippingAddresses } = await userApi.fetchShippingAddresses(accessToken)
      dispatch(fetchedShippingAddresses(shippingAddresses))

      const address = (
        Immutable.fromJS(shippingAddresses) as Immutable.List<KeyedGetter<ShippingAddress>>
      )
        .filter((addr) => addr?.get('shippingDefault') || false)
        .first()
      dispatch(basketAddressChange(address))
      dispatch(basketChosenAddressChange(address))
      dispatch(basketPostcodeChangePure(address.get('postcode')))

      dispatch(fetchShippingAddressesPending(false))
    } catch (err) {
      dispatch(fetchShippingAddressesError((err as any).message))
      dispatch(fetchShippingAddressesPending(false))
    }
  }
}

function userClearData() {
  return (dispatch: Dispatch<any>) => {
    dispatch({
      type: actionTypes.USER_CLEAR_DATA,
    })
    dispatch(basketAddressChange(null))
    dispatch(basketChosenAddressChange(null))
    dispatch(basketPostcodeChangePure(''))
  }
}

function userLoadOrder(orderId: string, forceRefresh = false) {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    dispatch(statusActions.pending(actionTypes.USER_LOAD_ORDERS, true))
    dispatch(statusActions.error(actionTypes.USER_LOAD_ORDERS, null))
    try {
      if (
        forceRefresh ||
        getState<{ user: KeyedGetter<{ orders: Immutable.List<UserOrder> }> }>()
          .user.get('orders')
          .find((order) => order?.get('id') === orderId) === undefined
      ) {
        const { data: order } = await orderV2.fetchOrder(getState, orderId)

        dispatch({
          type: actionTypes.USER_LOAD_ORDERS,
          orders: [order],
        })
      }
    } catch (err) {
      dispatch(statusActions.error(actionTypes.USER_LOAD_ORDERS, err))
      logger.error(err as any)
      throw err
    } finally {
      dispatch(statusActions.pending(actionTypes.USER_LOAD_ORDERS, false))
    }
  }
}

type OrderWithDeliveryDay = KeyedGetter<{ deliveryDay: string }>
type OrdersByIdOrDate<Order extends OrderWithDeliveryDay> = Immutable.Map<string, Order>

const getCalendarDate = (deliveryDay: string) => deliveryDay.split(/[ T]/)[0]

const getRealOrderDeliveryDates = <Order extends OrderWithDeliveryDay>(
  orders: OrdersByIdOrDate<Order>,
) =>
  orders
    .valueSeq()
    .map((order) => (order ? getCalendarDate(order.get('deliveryDay')) : null))
    .filter(Boolean)
    .toArray()

const removeProjectedDeliveriesForRealOrderDates = <
  ProjectedOrder extends OrderWithDeliveryDay,
  RealOrder extends OrderWithDeliveryDay,
>(
  projectedDeliveries: OrdersByIdOrDate<ProjectedOrder>,
  realOrders: OrdersByIdOrDate<RealOrder>,
) => {
  const realOrderDeliveryDates = getRealOrderDeliveryDates(realOrders)

  return projectedDeliveries.filter((projectedDelivery) =>
    projectedDelivery
      ? !realOrderDeliveryDates.includes(getCalendarDate(projectedDelivery.get('deliveryDay')))
      : false,
  )
}

function userLoadNewOrders(forceRefresh = false) {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    await Promise.all([
      dispatch(
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        userActions.userLoadOrdersForMyDeliveriesPage(forceRefresh, 'pending', 10, ['restorable']),
      ),
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      dispatch(userActions.userLoadProjectedDeliveries(forceRefresh)),
    ])

    const state = getState<{
      user: KeyedGetter<{
        restorableOrders: Immutable.List<UserOrder>
        projectedDeliveries: Immutable.List<ProjectedDelivery>
      }>
    }>()
    const pendingOrders = transformPendingOrders(
      state.user.get(
        'restorableOrders',
        // Refactoring note: function requires a list, but the default is OrderedMap, probably OrderedMap matches required fields
        Immutable.OrderedMap({}) as unknown as Immutable.List<UserOrder>,
      ),
    )
    const projectedDeliveries = transformProjectedDeliveries(state.user.get('projectedDeliveries'))
    const projectedDeliveriesWithoutRealOrders = removeProjectedDeliveriesForRealOrderDates(
      projectedDeliveries,
      pendingOrders,
    )
    const ordersCombined = pendingOrders.merge(projectedDeliveriesWithoutRealOrders)

    dispatch({ type: actionTypes.MYDELIVERIES_ORDERS, orders: ordersCombined })
  }
}

function userPromoApplyCode(promoCode: string) {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    const accessToken =
      getState<{ auth: KeyedGetter<{ accessToken: string }> }>().auth.get('accessToken')
    const state = getState<{
      promoAgeVerified: boolean
      user: KeyedGetter<{ ageVerified: boolean }>
    }>()
    if (state.promoAgeVerified && !state.user.get('ageVerified')) {
      await dispatch(userVerifyAge(state.promoAgeVerified, true))
    }
    try {
      await userApi.applyPromo(accessToken, promoCode)
    } catch (err) {
      dispatch(statusActions.error(actionTypes.PROMO_APPLY, (err as any).message))
    }
  }
}

export function userProspect() {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    const state = getState<
      CheckoutStore & {
        routing: {
          locationBeforeTransitions: {
            pathname: string
          }
        }
        basket: BasketState
      }
    >()
    const { routing } = state
    try {
      const step = routing.locationBeforeTransitions.pathname.split('/').pop() as string

      const email = getEmail(state)
      const promocode = getPromoCode(state)
      const { firstName, lastName } = getName(state)

      const reqData = {
        email,
        promocode: promocode as string,
        step,
        user_name_first: firstName?.trim() ?? '',
        user_name_last: lastName?.trim() ?? '',
        allow_marketing_email: getIsEmailMarketingEnabled(state),
        preview_order_id: getPreviewOrderId(state),
      }
      dispatch(statusActions.pending(actionTypes.USER_PROSPECT, true))
      dispatch(statusActions.error(actionTypes.USER_PROSPECT, false))

      if (step === 'account') {
        const allowMarketingEmail = getIsEmailMarketingEnabled(state)
        const previewOrderId = state.basket.get('previewOrderId')

        dispatch(
          trackSignupStarted({
            email,
            promocode,
            allowMarketingEmail,
            previewOrderId,
          }),
        )
      }

      await prospectApi.storeProspect(reqData)
    } catch (err) {
      dispatch(statusActions.errorLoad(actionTypes.USER_PROSPECT, err))
      throw err
    } finally {
      dispatch(statusActions.pending(actionTypes.USER_PROSPECT, false))
    }

    return null
  }
}

function userOpenCloseOrderCard(orderId: string, isCollapsed: boolean) {
  return (dispatch: Dispatch<any>) => {
    dispatch({
      type: actionTypes.USER_ORDER_CARD_OPEN_CLOSE,
      orderId,
      isCollapsed,
    })
  }
}

function userToggleEditDateSection(orderId: string, editDeliveryMode: boolean) {
  return (dispatch: Dispatch<any>) => {
    dispatch({
      type: actionTypes.USER_ORDER_EDIT_OPEN_CLOSE,
      orderId,
      editDeliveryMode,
    })
  }
}

function userTrackToggleEditDateSection(orderId: string) {
  return (dispatch: Dispatch<any>, getState: GetState) => {
    const originalSlotId = getState<{
      user: KeyedGetter<{
        newOrders: Immutable.Map<string, KeyedGetter<{ deliverySlotId: string }>>
      }>
    }>().user.getIn(['newOrders', orderId, 'deliverySlotId'])

    dispatch({
      type: actionTypes.TRACKING,
      trackingData: {
        actionType: 'OrderDeliverySlot Edit',
        order_id: orderId,
        original_deliveryslot_id: originalSlotId,
      },
    })
  }
}

function userTrackToggleEditAddressSection(orderId: string) {
  return (dispatch: Dispatch<any>, getState: GetState) => {
    const originalAddressId = getState<{
      user: KeyedGetter<{
        newOrders: Immutable.Map<string, KeyedGetter<{ shippingAddressId: string }>>
      }>
    }>().user.getIn(['newOrders', orderId, 'shippingAddressId'])

    dispatch({
      type: actionTypes.TRACKING,
      trackingData: {
        actionType: 'OrderDeliveryAddress Edit',
        order_id: orderId,
        original_deliveryaddress_id: originalAddressId,
      },
    })
  }
}

function userTrackDateSelected(orderId: string, originalSlotId: string, newSlotId: string) {
  return (dispatch: Dispatch<any>) => {
    dispatch({
      type: actionTypes.TRACKING,
      trackingData: {
        actionType: 'OrderDeliveryDate Selected',
        order_id: orderId,
        original_deliveryslot_id: originalSlotId,
        new_deliveryslot_id: newSlotId,
      },
    })
  }
}

function userTrackSlotSelected(orderId: string, originalSlotId: string, newSlotId: string) {
  return (dispatch: Dispatch<any>) => {
    dispatch({
      type: actionTypes.TRACKING,
      trackingData: {
        actionType: 'OrderDeliverySlot Selected',
        order_id: orderId,
        original_deliveryslot_id: originalSlotId,
        new_deliveryslot_id: newSlotId,
      },
    })
  }
}

function userTrackAddressSelected(
  orderId: string,
  originalAddressId: string,
  newAddressId: string,
) {
  return (dispatch: Dispatch<any>) => {
    dispatch({
      type: actionTypes.TRACKING,
      trackingData: {
        actionType: 'OrderDeliveryAddress Selected',
        order_id: orderId,
        original_deliveryaddress_id: originalAddressId,
        new_deliveryaddress_id: newAddressId,
      },
    })
  }
}

function userUnsubscribe({
  authUserId,
  marketingType,
  marketingUnsubscribeToken,
}: {
  authUserId: string
  marketingType: string
  marketingUnsubscribeToken: string
}) {
  return async (dispatch: Dispatch<any>) => {
    dispatch(statusActions.pending(actionTypes.UNSUBSCRIBED_USER, true))
    dispatch(statusActions.error(actionTypes.UNSUBSCRIBED_USER, ''))

    try {
      await userApi.deleteMarketingSubscription(
        authUserId,
        marketingType,
        marketingUnsubscribeToken,
      )

      dispatch({
        type: actionTypes.UNSUBSCRIBED_USER,
      })
    } catch (err) {
      dispatch(statusActions.error(actionTypes.UNSUBSCRIBED_USER, err))
    } finally {
      dispatch(statusActions.pending(actionTypes.UNSUBSCRIBED_USER, false))
    }
  }
}

export function userFetchReferralOffer() {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    dispatch(statusActions.pending(actionTypes.USER_LOAD_REFERRAL_OFFER, true))
    const accessToken =
      getState<{ auth: KeyedGetter<{ accessToken: string }> }>().auth.get('accessToken')
    if (accessToken) {
      const { data: referralOffer } = await userApi.fetchReferralOffer(accessToken)
      dispatch({ type: actionTypes.USER_LOAD_REFERRAL_OFFER, referralOffer })
      dispatch(statusActions.pending(actionTypes.USER_LOAD_REFERRAL_OFFER, false))
    }
  }
}

export const userGetReferralDetails = () => async (dispatch: Dispatch<any>, getState: GetState) => {
  try {
    const accessToken =
      getState<{ auth: KeyedGetter<{ accessToken: string }> }>().auth.get('accessToken')
    const { data } = await userApi.referralDetails(accessToken)

    dispatch(userLoadReferralDetails(data))
  } catch (err) {
    logger.error({ message: 'Failed to fetch referral details', errors: [err] })
  }
}

export const trackingReferFriend =
  (actionType: string, trackingType?: string) => (dispatch: Dispatch<any>) => {
    if (actionType && trackingType) {
      dispatch({
        type: actionType,
        trackingData: {
          actionType: trackingType,
        },
      })
    }
  }

export const trackingReferFriendSocialSharing =
  (actionType: string, trackingType: string, channel: string) => (dispatch: Dispatch<any>) => {
    if (actionType && trackingType) {
      dispatch({
        type: actionType,
        trackingData: {
          actionType: trackingType,
          channel,
        },
      })
    }
  }

export const userLoadOrderTrackingsInfo =
  (orderId: string) => async (dispatch: Dispatch<any>, getState: GetState) => {
    dispatch(statusActions.pending(actionTypes.USER_LOAD_ORDER_TRACKINGS, true))
    dispatch(statusActions.error(actionTypes.USER_LOAD_ORDER_TRACKINGS, false))
    try {
      const accessToken =
        getState<{ auth: KeyedGetter<{ accessToken: string }> }>().auth.get('accessToken')
      const orderTracking = await fetchDeliveryConsignment(accessToken, orderId)
      const trackingUrls = orderTracking.data.map(({ trackingUrl }) => trackingUrl)
      dispatch({
        type: actionTypes.USER_LOAD_ORDER_TRACKINGS,
        trackingUrls,
      })
    } catch (err) {
      dispatch(statusActions.error(actionTypes.USER_LOAD_ORDER_TRACKINGS, true))
      dispatch({
        type: actionTypes.USER_LOAD_ORDER_TRACKINGS,
        trackingUrls: [],
      })
      logger.error({ message: `Failed to fetch tracking urls for order ${orderId}`, errors: [err] })
    } finally {
      dispatch(statusActions.pending(actionTypes.USER_LOAD_ORDER_TRACKINGS, false))
    }
  }

const userActions = {
  userFetchCredit,
  userOrderCancelNext,
  userOrderSkipNextProjected,
  userLoadData,
  userLoadReferralDetails,
  userFetchShippingAddresses,
  userClearData,
  userLoadOrder,
  userLoadOrders,
  userLoadNewOrders,
  userLoadOrdersForMyDeliveriesPage,
  userLoadProjectedDeliveries,
  userPromoApplyCode,
  userVerifyAge,
  userProspect,
  userOpenCloseOrderCard,
  userToggleEditDateSection,
  userTrackToggleEditDateSection,
  userTrackToggleEditAddressSection,
  userTrackDateSelected,
  userTrackSlotSelected,
  userTrackAddressSelected,
  userUnsubscribe,
  userFetchReferralOffer,
  userLoadOrdersForMyGousto,
}

// eslint-disable-next-line import/no-default-export
export default userActions
